import { z } from 'zod';

import type { TFunction } from 'i18next';

import { stripHtml } from '@/helpers';

import { KEY_TYPE_LIQPAY, KEY_TYPE_MONOPAY } from '@/constants';

const serviceSchema = z.object({
  id: z.number(),
  name: z.string(),
  price: z.number(),
  initial_count: z.number().optional(),
});

export function createBookingServiceFormSchema(
  t: TFunction,
  options: { hasLiqpayKey: boolean },
) {
  const { hasLiqpayKey } = options;

  return z
    .object({
      name: z.string().refine((val) => stripHtml(val).trim().length > 0, {
        message: t('validation_required'),
      }),
      description: z.string(),
      location_ids: z.array(z.string()),
      services: z.array(serviceSchema),
      booking_service_type: z
        .string()
        .min(1, { message: t('validation_required') }),
      [KEY_TYPE_LIQPAY]: z.string().nullable(),
      [KEY_TYPE_MONOPAY]: z.string().nullable(),
      order_position: z.string().optional(),
      duration_units_count: z
        .string()
        .min(1, { message: t('validation_required') }),
      color: z.string().min(1),
      photo: z.any().nullable().optional(),
      active: z.boolean(),
      available_for_online: z.boolean(),
    })
    .superRefine((data, ctx) => {
      if (data.location_ids.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('validation_required'),
          path: ['location_ids'],
        });
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('validation_select_locations_first'),
          path: ['services'],
        });
      } else if (data.services.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('validation_required'),
          path: ['services'],
        });
      }
    })
    .superRefine((data, ctx) => {
      if (hasLiqpayKey) {
        if (data[KEY_TYPE_LIQPAY] == null || data[KEY_TYPE_LIQPAY] === '') {
          ctx.addIssue({
            code: 'custom',
            message: t('validation_required'),
            path: [KEY_TYPE_LIQPAY],
          });
        }
      } else {
        if (data[KEY_TYPE_MONOPAY] == null || data[KEY_TYPE_MONOPAY] === '') {
          ctx.addIssue({
            code: 'custom',
            message: t('validation_required'),
            path: [KEY_TYPE_MONOPAY],
          });
        }
      }
    });
}

type BookingServiceFormSchema = ReturnType<
  typeof createBookingServiceFormSchema
>;

export type BookingServiceFormValues = z.infer<BookingServiceFormSchema>;
